import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";
import {
  getAllAssetMovements,
  scanAssetTag,
  type AssetMovement,
} from "../../../service/securityService";
import type { LocationType } from "../../../types/api";

type TaggedAsset = {
  id: string;
  name: string;
  equipmentId?: string;
  serialNumber?: string;
  premisesName?: string;
  floor?: string;
  location?: string;
  assetTag?: string;
  rfidTag?: string;
  barcode?: string;
  qrCode?: string;
};

const ZONE_PRESETS: Array<{
  label: string;
  location: string;
}> = [
  { label: "Building A - Exit", location: "Building_A_Exit_Gate_1" },
  { label: "Building A - Entry", location: "Building_A_Entry_Gate_1" },
  { label: "Building B - Entry", location: "Building_B_Entry_Gate_1" },
  { label: "Building B - Exit", location: "Building_B_Exit_Gate_1" },
  { label: "Internal - Store", location: "Premises_Main_Store_Entry" },
  {
    label: "Internal - Maintenance",
    location: "Premises_Maintenance_Bay_Entry",
  },
  { label: "Internal - Radiology", location: "Radiology_Entry" },
];

const RfidDemoConsole: React.FC = () => {
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assets, setAssets] = useState<TaggedAsset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string>("");
  const [showTaggedOnly, setShowTaggedOnly] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");
  const [assetsError, setAssetsError] = useState<string>("");

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedAssetId) || null,
    [assets, selectedAssetId],
  );

  const [tagValue, setTagValue] = useState("");
  const [rfidTagDraft, setRfidTagDraft] = useState("");
  const [assigningTag, setAssigningTag] = useState(false);

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");

  const [zoneId, setZoneId] = useState("");
  const [location, setLocation] = useState(ZONE_PRESETS[0]?.location || "");
  const [simulating, setSimulating] = useState(false);
  const [lastResponse, setLastResponse] = useState<any>(null);

  const [loadingMovements, setLoadingMovements] = useState(false);
  const [recentMovements, setRecentMovements] = useState<AssetMovement[]>([]);

  const formatLocationOptionLabel = (l: LocationType) => {
    const room = l.room ? `Room: ${l.room}` : "";
    const zone = l.zone ? `Zone: ${l.zone}` : "";
    const floor = l.floorNumber ? `Floor: ${l.floorNumber}` : "";
    const code = l.locationCode ? `Code: ${l.locationCode}` : "";
    const parts = [code, room, zone, floor].filter(Boolean);
    return parts.length ? parts.join(" | ") : l._id || "Unknown location";
  };

  const formatLocationPayload = (l: LocationType) => {
    // Human-friendly string stored in movement `toLocation`
    const parts = [l.room, l.zone, l.locationCode].filter(Boolean);
    return parts.length ? parts.join(" - ") : l.room || l._id || "Unknown";
  };

  const movementColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Detected at",
        field: "detectedAt",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => params.data?.detectedAt || "",
      },
      {
        headerName: "Asset",
        field: "assetName",
        flex: 1.2,
        minWidth: 220,
        valueGetter: (params) => {
          const name = params.data?.assetName || "—";
          const eqId = params.data?.equipmentId;
          return eqId ? `${name} (${eqId})` : name;
        },
      },
      {
        headerName: "Tag",
        field: "assetTag",
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => params.data?.assetTag || "—",
      },
      {
        headerName: "From",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => {
          const fromLocation = params.data?.fromLocation;
          const fromZoneName = (params.data as any)?.fromZoneName;
          const fromZone = params.data?.fromZone;
          if (fromLocation && fromLocation !== "Unknown") return fromLocation;
          if (fromZoneName) return fromZoneName;
          if (fromZone) return `Zone: ${fromZone}`;
          return "—";
        },
      },
      {
        headerName: "To",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => {
          const toLocation = params.data?.toLocation;
          const toZoneName = (params.data as any)?.toZoneName;
          const toZone = params.data?.toZone;
          if (toLocation && toLocation !== "Unknown") return toLocation;
          if (toZoneName) return toZoneName;
          if (toZone) return `Zone: ${toZone}`;
          return "—";
        },
      },
      {
        headerName: "Type",
        field: "movementType",
        flex: 0.8,
        minWidth: 120,
        valueGetter: (params) => params.data?.movementType || "—",
      },
    ],
    [],
  );

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await axiosInstance.get("/api/location/all");
      const list = (response.data?.data || []) as LocationType[];
      setLocations(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load locations:", err);
      toast.error("Failed to load Location Master list");
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAssets = async () => {
    setLoadingAssets(true);
    setAssetsError("");
    try {
      const params: Record<string, string | number> = { limit: 200 };
      if (showTaggedOnly) params.tagged = "true";
      if (assetSearch.trim()) params.search = assetSearch.trim();

      const response = await axiosInstance.get("/api/security/assets/list", {
        params,
      });
      if (response.data?.success) {
        setAssets((response.data.data || []) as TaggedAsset[]);
      } else {
        setAssets([]);
        setAssetsError(response.data?.message || "No equipment returned.");
      }
    } catch (err) {
      console.error("Failed to load assets list:", err);
      toast.error("Failed to load equipment list for demo");
      setAssets([]);
      setAssetsError("Failed to load equipment list (API error).");
    } finally {
      setLoadingAssets(false);
    }
  };

  const fetchRecentMovements = async (searchQuery?: string) => {
    setLoadingMovements(true);
    try {
      const result = await getAllAssetMovements(
        searchQuery ? { searchQuery } : undefined,
        { page: 1, limit: 10 },
      );
      setRecentMovements(result.data || []);
    } catch (err) {
      console.error("Failed to load movements:", err);
      setRecentMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchRecentMovements();
  }, [showTaggedOnly]);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    const next =
      selectedAsset?.rfidTag ||
      selectedAsset?.assetTag ||
      selectedAsset?.barcode ||
      selectedAsset?.qrCode ||
      "";
    setTagValue(next);
    setRfidTagDraft(selectedAsset?.rfidTag || "");
  }, [selectedAsset]);

  const handleAssignRfidTag = async () => {
    if (!selectedAsset) {
      toast.info("Select equipment first");
      return;
    }
    if (!rfidTagDraft.trim()) {
      toast.error("Enter an RFID tag value");
      return;
    }

    setAssigningTag(true);
    try {
      const response = await axiosInstance.post(
        `/api/security/assets/${selectedAsset.id}/assign-tag`,
        {
          rfidTag: rfidTagDraft.trim(),
          securityStatus: "Normal",
          isRestricted: false,
        },
      );

      if (response.data?.success) {
        toast.success("RFID tag assigned");
        await fetchAssets();
        setTagValue(rfidTagDraft.trim());
      } else {
        toast.error(response.data?.message || "Failed to assign RFID tag");
      }
    } catch (err: any) {
      console.error("Assign tag failed:", err);
      toast.error(
        err?.response?.data?.message || "Failed to assign RFID tag (API error)",
      );
    } finally {
      setAssigningTag(false);
    }
  };

  const handleSimulateRead = async () => {
    if (!selectedAsset) {
      toast.info("Select equipment first");
      return;
    }
    if (!tagValue.trim()) {
      toast.error("No tag value found. Assign an RFID tag first.");
      return;
    }
    if (!zoneId.trim()) {
      toast.error("Select a Zone (Location Master) or enter a Zone ID");
      return;
    }
    if (!location.trim()) {
      toast.error("Enter Location label");
      return;
    }

    setSimulating(true);
    try {
      const resp = await scanAssetTag(tagValue.trim(), zoneId.trim(), location);
      setLastResponse(resp);
      toast.success("Simulated read sent");
      await fetchRecentMovements(tagValue.trim());
    } catch (err: any) {
      console.error("scan-tag failed:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to simulate read",
      );
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="px-4 py-4 mt-14 w-full h-full">
      <div className="flex flex-col gap-1 mb-4">
        <h2 className="text-xl font-semibold">RFID Demo Console</h2>
        {/* <p className="text-sm text-gray-600">
          Demo-only screen. It simulates RFID reader events by calling{" "}
          <code className="px-1 py-[2px] bg-gray-100 rounded">
            POST /api/security/asset-movements/scan-tag
          </code>
          .
        </p> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold mb-3">1) Select equipment</h3>

          <div className="flex flex-wrap gap-2 items-center mb-3">
            <button
              onClick={fetchAssets}
              disabled={loadingAssets}
              className="px-3 py-2 rounded bg-[#0fa9a6] text-white font-semibold disabled:opacity-50"
            >
              {loadingAssets ? "Refreshing..." : "Refresh list"}
            </button>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showTaggedOnly}
                onChange={(e) => setShowTaggedOnly(e.target.checked)}
              />
              Show tagged only
            </label>
          </div>

          <label className="block text-sm font-medium mb-1">Search</label>
          <div className="flex gap-2 mb-3">
            <input
              value={assetSearch}
              onChange={(e) => setAssetSearch(e.target.value)}
              placeholder="Search by name or tag..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
            />
            <button
              onClick={fetchAssets}
              disabled={loadingAssets}
              className="px-3 py-2 rounded border border-gray-300 font-semibold disabled:opacity-50"
            >
              Search
            </button>
          </div>

          <label className="block text-sm font-medium mb-1">Equipment</label>
          <select
            value={selectedAssetId}
            onChange={(e) => setSelectedAssetId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">
              {showTaggedOnly
                ? "Select tagged equipment..."
                : "Select equipment..."}
            </option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} {a.equipmentId ? `(${a.equipmentId})` : ""}
              </option>
            ))}
          </select>

          {assets.length === 0 && (
            <div className="mt-3 text-sm text-gray-600">
              <div className="font-medium">No equipment found.</div>
              <div className="mt-1">
                Tips:
                <ul className="list-disc pl-5">
                  <li>
                    Turn off <b>Show tagged only</b> to see all equipment, then
                    assign an RFID tag here.
                  </li>
                  <li>
                    Or go to <b>Security → Asset &amp; Equipment Security</b> to
                    assign tags first.
                  </li>
                </ul>
              </div>
              {assetsError ? (
                <div className="mt-2 text-xs text-gray-500">{assetsError}</div>
              ) : null}
            </div>
          )}

          {selectedAsset && (
            <div className="mt-3 text-sm text-gray-700 space-y-1">
              <div>
                <span className="font-medium">Equipment ID:</span>{" "}
                {selectedAsset.equipmentId || "—"}
              </div>
              <div>
                <span className="font-medium">Serial:</span>{" "}
                {selectedAsset.serialNumber || "—"}
              </div>
              <div>
                <span className="font-medium">Premises:</span>{" "}
                {selectedAsset.premisesName || "—"}
              </div>
              <div>
                <span className="font-medium">Last known:</span>{" "}
                {selectedAsset.location || "—"}{" "}
                {selectedAsset.floor ? `(${selectedAsset.floor})` : ""}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold mb-3">2) Tag & simulate reader event</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                RFID tag (assign / update)
              </label>
              <div className="flex gap-2">
                <input
                  value={rfidTagDraft}
                  onChange={(e) => setRfidTagDraft(e.target.value)}
                  placeholder="E2000017221101441890ABCD"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  disabled={!selectedAsset || assigningTag}
                />
                <button
                  onClick={handleAssignRfidTag}
                  disabled={!selectedAsset || assigningTag}
                  className="px-3 py-2 rounded bg-blue-600 text-white font-semibold disabled:opacity-50"
                >
                  {assigningTag ? "Assigning..." : "Assign"}
                </button>
              </div>
              {/* <p className="text-xs text-gray-500 mt-1">
                This calls{" "}
                <code className="px-1 py-[2px] bg-gray-100 rounded">
                  POST /api/security/assets/:assetId/assign-tag
                </code>
                .
              </p> */}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                Tag value used for scan-tag
              </label>
              <input
                value={tagValue}
                onChange={(e) => setTagValue(e.target.value)}
                placeholder="Will use RFID tag by default"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!selectedAsset}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                Zone preset (sets Location label)
              </label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {ZONE_PRESETS.map((z) => (
                  <option key={z.location} value={z.location}>
                    {z.label} — {z.location}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <label className="block text-sm font-medium mb-1">
                  Zone (Location Master)
                </label>
                <button
                  type="button"
                  onClick={fetchLocations}
                  disabled={loadingLocations}
                  className="text-xs px-2 py-1 rounded border border-gray-300 disabled:opacity-50"
                >
                  {loadingLocations ? "Refreshing..." : "Refresh locations"}
                </button>
              </div>
              <select
                value={selectedLocationId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedLocationId(id);
                  const selected = locations.find((l) => l._id === id);
                  if (selected?._id) {
                    setZoneId(selected._id);
                    setLocation(formatLocationPayload(selected));
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select a location (zone)...</option>
                {locations
                  .filter((l) => Boolean(l._id))
                  .map((l) => (
                    <option key={l._id} value={l._id}>
                      {formatLocationOptionLabel(l)}
                    </option>
                  ))}
              </select>
              {/* <p className="text-xs text-gray-500 mt-1">
                Selecting a Location Master entry will auto-fill <b>Zone ID</b>{" "}
                with its <code>_id</code> and set <b>Location</b> label for the
                movement.
              </p> */}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Zone ID</label>
              <input
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                placeholder="Auto-filled from Location Master selection"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSimulateRead}
              disabled={simulating}
              className="px-4 py-2 rounded bg-[#0fa9a6] text-white font-semibold disabled:opacity-50"
            >
              {simulating ? "Sending..." : "Simulate RFID read"}
            </button>
            <button
              onClick={() => fetchRecentMovements(tagValue.trim() || undefined)}
              disabled={loadingMovements}
              className="px-4 py-2 rounded border border-gray-300 font-semibold disabled:opacity-50"
            >
              {loadingMovements ? "Loading..." : "Refresh movements"}
            </button>
          </div>

          {lastResponse && (
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Last response</h4>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-60">
                {JSON.stringify(lastResponse, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow p-4 mt-4">
        <h3 className="font-semibold mb-3">Recent movements</h3>
        <div className="ag-theme-alpine bg-white rounded">
          <AgGridReact
            columnDefs={movementColumnDefs}
            rowData={recentMovements as any[]}
            rowHeight={44}
            headerHeight={44}
            suppressCellFocus={true}
            animateRows={true}
            domLayout="autoHeight"
            overlayNoRowsTemplate={
              loadingMovements ? "Loading movements..." : "No movements found."
            }
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: false,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RfidDemoConsole;
